import * as React from "react";
import styled from "@emotion/styled";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import {
  Link,
  List,
  H1,
  Button,
  IconChevron,
  Stack,
  IconMenu,
  FlexRow,
} from "atoms";
import { useAuth } from "features/auth";
import { useToggle } from "utils/hooks";

function AdminWrapper() {
  const {
    roles: { isAdmin, isSuperAdmin },
  } = useAuth();

  const { pathname } = useLocation();

  const [isOpen, toggleIsOpen, setIsOpen] = useToggle(false);

  React.useEffect(() => {
    setIsOpen(false);
  }, [pathname, setIsOpen]);

  if (!isAdmin && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <Wrapper>
      <AdminNav>
        <FlexRow justifyContent="space-between">
          <H1 size="h3">Admin</H1>
          <Button
            variant="secondary"
            size="xxs"
            aria-expanded={isOpen}
            aria-controls="admin-nav-list"
            onClick={toggleIsOpen}
          >
            <IconMenu /> Admin Menu
          </Button>
        </FlexRow>

        <AdminNavList type="blank" id="admin-nav-list" isOpen={isOpen}>
          <li>
            <AdminNavLink
              to="/admin/users"
              isActive={pathname.startsWith("/admin/users")}
            >
              User Search
            </AdminNavLink>
          </li>
          <li>
            <AdminNavLink
              to="/admin/reports"
              isActive={pathname.startsWith("/admin/reports")}
            >
              Curator Reports
            </AdminNavLink>
          </li>
          <li>
            <AdminNavLink
              to="/admin/raffles"
              isActive={pathname === "/admin/raffles"}
            >
              Raffles
            </AdminNavLink>
          </li>
          <li>
            <AdminNavLink
              to="/admin/applications"
              isActive={pathname.startsWith("/admin/applications")}
            >
              Artist Applications
            </AdminNavLink>
          </li>
          <li>
            <AdminNavLink
              to="/admin/collectives"
              isActive={pathname.startsWith("/admin/collectives")}
            >
              Collectives
            </AdminNavLink>
          </li>
          <li>
            <AdminNavLink
              to="/admin/create-update"
              isActive={pathname === "/admin/create-update"}
            >
              Post an Update
            </AdminNavLink>
          </li>
          <li>
            <AdminNavLink
              to="/admin/open-calls"
              isActive={pathname.startsWith("/admin/open-calls")}
            >
              Open Calls
            </AdminNavLink>
          </li>
          <AdminSubnavToggleButton
            label="Edit Home Page"
            isActive={pathname.includes("edit-home")}
          >
            <li>
              <AdminNavLink
                to="/admin/edit-home-hero"
                isActive={pathname === "/admin/edit-home-hero"}
              >
                Edit: Home Hero
              </AdminNavLink>
            </li>
            <li>
              <AdminNavLink
                to="/admin/edit-home-discover-tag"
                isActive={pathname === "/admin/edit-home-discover-tag"}
              >
                Edit: Discover
              </AdminNavLink>
            </li>
            <li>
              <AdminNavLink
                to="/admin/edit-home-featured-artist"
                isActive={pathname === "/admin/edit-home-featured-artist"}
              >
                Edit: Featured
              </AdminNavLink>
            </li>
            <li>
              <AdminNavLink
                to="/admin/edit-home-highlight"
                isActive={pathname === "/admin/edit-home-highlight"}
              >
                Edit: Highlight
              </AdminNavLink>
            </li>
            <li>
              <AdminNavLink
                to="/admin/edit-home-publish"
                isActive={pathname === "/admin/edit-home-publish"}
              >
                Edit: Publish Draft
              </AdminNavLink>
            </li>
          </AdminSubnavToggleButton>
          <li>
            <AdminNavLink
              to="/admin/hug-points"
              isActive={pathname === "/admin/hug-points"}
            >
              HUG Points
            </AdminNavLink>
          </li>
        </AdminNavList>
      </AdminNav>

      <OutletWrapper>
        <Outlet />
      </OutletWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div(({ theme }) => ({
  [theme.breakpoints.desktop]: {
    display: "flex",
  },
}));

const childPadding = "0.5rem 0.5rem 0.5rem 1rem";

const AdminNav = styled.nav(({ theme }) => ({
  borderBottom: `1px solid ${theme.colors.fg10}`,
  display: "flex",
  flexDirection: "column",
  flex: "0 1 240px",
  gap: "0 10px",
  padding: `1.5rem ${theme.spacing.gutter}`,
  position: "relative",
  h1: { margin: 0 },
  [theme.breakpoints.desktop]: {
    border: 0,
    borderRight: `2px solid ${theme.colors.fg20}`,
    minWidth: 240,
    "h1+button": { display: "none" },
    padding: "2rem 0",
    h1: {
      paddingLeft: "1rem",
      paddingBottom: "1rem",
    },
  },
}));

const AdminNavList = styled(List, {
  shouldForwardProp: (prop) => prop !== "isOpen",
})<{ isOpen: boolean }>(({ isOpen, theme }) => ({
  border: 0,
  margin: "10px 0 0 auto",
  padding: 0,
  width: "100%",
  display: "block",
  [theme.breakpoints.maxDesktop]: {
    backgroundColor: theme.colors.bg,
    border: 0,
    borderRadius: theme.borderRadius.sm,
    boxShadow: theme.boxShadow.dark,
    margin: 0,
    maxWidth: `calc(100vw - ( ${theme.spacing.gutter} * 2))`,
    width: 320,
    position: "absolute",
    right: theme.spacing.gutter,
    top: "calc(100% - 1rem)",
    zIndex: 5,
    ...(isOpen
      ? {
          display: "block",
        }
      : {
          display: "none",
        }),
  },
  "&>li>a, &>li>button": {
    boxShadow: `inset 0 -1px 0 0 ${theme.colors.fg20}`,
  },
}));

type WithIsActive = { isActive: boolean };

const AdminNavLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<WithIsActive>(({ isActive, theme }) => ({
  ...(isActive && {
    border: 0,
    borderLeft: "3px solid rgba(0,0,0,0)",
    backgroundColor: theme.colors.accent3LL,
    borderColor: theme.colors.accent3,
  }),
  display: "block",
  fontSize: 15,
  fontWeight: "bold",
  marginLeft: -2,
  padding: "0.5rem 0.75rem",
  textDecoration: "none",
  [theme.breakpoints.desktop]: {
    ...(isActive && {
      backgroundColor: theme.colors.accent3LL,
      border: 0,
      borderRight: "4px solid rgba(0,0,0,0)",
      borderColor: theme.colors.accent3,
      marginRight: -3,
    }),
    marginLeft: 0,
    padding: childPadding,
  },
  "&&:hover": {
    color: theme.colors.fg,
    backgroundColor: `${theme.colors.fg}22`,
    ...(isActive && {
      backgroundColor: `${theme.colors.accent3}87`,
    }),
  },
}));

const OutletWrapper = styled.div(({ theme }) => ({
  flex: "1 1 auto",
  padding: `2rem ${theme.spacing.gutter}`,
  minHeight: "75vh",
}));

function AdminSubnavToggleButton({
  label,
  isActive,
  children,
}: {
  label: string;
  isActive: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, toggleIsOpen, setIsOpen] = useToggle(isActive);

  React.useEffect(() => {
    setIsOpen(isActive);
  }, [setIsOpen, isActive]);

  const Id = `${label}-menu`;
  return (
    <li>
      <SButton
        variant="text"
        size="xs"
        aria-controls={`#${Id}`}
        onClick={toggleIsOpen}
        isActive={isActive}
        isOpen={isOpen}
      >
        <strong>{label}</strong> <IconChevron />
      </SButton>
      <AdminSubNav hidden={!isOpen} type="blank" id={Id}>
        {children}
      </AdminSubNav>
    </li>
  );
}

const SButton = styled(Button, {
  shouldForwardProp: (prop: string) => !prop.startsWith("is"),
})<{ isActive: boolean; isOpen: boolean }>(({ theme, isActive, isOpen }) => ({
  border: 0,
  fontSize: 15,
  padding: "0.5rem 0.75rem",
  width: "100%",
  justifyContent: "space-between",
  textAlign: "left",
  "&, &&:hover, &&:focus-visible": {
    textDecoration: "none",
  },
  "&&:hover": {
    color: theme.colors.fg,
    backgroundColor: `${theme.colors.fg}22`,
  },
  ...(isActive && {
    backgroundColor: isOpen ? theme.colors.bg : `${theme.colors.fg}11`,
    ...(!isOpen && {
      boxShadow: `inset 0 -1px 0 0 ${theme.colors.fg30}`,
    }),
  }),
  ...(isOpen && {
    "&&": {
      boxShadow: "none",
    },
  }),
  [theme.breakpoints.desktop]: {
    border: 0,
    padding: childPadding,
  },
  svg: {
    fontSize: 13,
    transform: `rotate(${isOpen ? "0deg" : "180deg"})`,
  },
}));
const AdminSubNav = styled(List)(({ theme }) => ({
  padding: "5px 0 10px",
  boxShadow: `inset 0 -1px 0 0 ${theme.colors.fg20}`,
  a: {
    fontSize: 13,
    lineHeight: 1.2,
  },
  "a::before": {
    color: theme.colors.fg60,
    content: "'\\21B3'",
    fontSize: 10,
    paddingRight: 4,
  },
}));

const AdminPageHeader = styled(Stack)({
  marginBottom: "2rem",
}).withComponent("header");
AdminPageHeader.defaultProps = { gap: "xs" };

export default AdminWrapper;
export { AdminPageHeader };
